/**
 * KOAI Chat Chart Component
 * 챗에서 표시되는 차트 컴포넌트
 * 지원: 막대그래프, 히스토그램, 원그래프, 박스플롯, 산점도, 선그래프
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';

const ChatChart = ({ chartData }) => {
  const plotRef = useRef(null);
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [renderError, setRenderError] = useState(null);

  useEffect(() => {
    if (chartData && chartData.data) {
      // 기존 차트 정리
      destroyChart();
      // 약간의 딜레이 후 렌더링 (DOM 업데이트 대기)
      const timer = setTimeout(() => renderChart(), 100);
      return () => clearTimeout(timer);
    }
  }, [chartData]);

  const destroyChart = () => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }
    if (plotRef.current) {
      if (window.Plotly) {
        try { window.Plotly.purge(plotRef.current); } catch (e) {}
      }
    }
  };

  const renderChart = () => {
    if (!chartData || !chartData.data) return;
    
    const { chartType, data } = chartData;
    setRenderError(null);

    try {
      switch (chartType) {
        case 'bar':
          renderBar(data);
          break;
        case 'pie':
          renderPie(data);
          break;
        case 'histogram':
          renderHistogram(data);
          break;
        case 'line':
          renderLine(data);
          break;
        case 'boxplot':
          renderBoxplot(data);
          break;
        case 'scatter':
          renderScatter(data);
          break;
        default:
          renderBar(data);
      }
    } catch (error) {
      console.error('차트 렌더링 오류:', error);
      setRenderError(error.message);
    }
  };

  const renderBar = (data) => {
    if (!window.Chart || !chartRef.current) return;
    const ctx = chartRef.current.getContext('2d');

    chartInstanceRef.current = new window.Chart(ctx, {
      type: 'bar',
      data: {
        labels: (data.labels || []).slice(0, 15),
        datasets: [{
          label: data.columnName || '값',
          data: (data.values || []).slice(0, 15),
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 99, 132, 0.7)',
            'rgba(75, 192, 192, 0.7)',
            'rgba(255, 206, 86, 0.7)',
            'rgba(153, 102, 255, 0.7)',
            'rgba(255, 159, 64, 0.7)',
            'rgba(199, 199, 199, 0.7)',
            'rgba(83, 102, 255, 0.7)',
            'rgba(255, 99, 255, 0.7)',
            'rgba(99, 255, 132, 0.7)',
            'rgba(54, 162, 235, 0.5)',
            'rgba(255, 99, 132, 0.5)',
            'rgba(75, 192, 192, 0.5)',
            'rgba(255, 206, 86, 0.5)',
            'rgba(153, 102, 255, 0.5)',
          ],
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: `막대그래프 - ${chartData.file?.name || ''}` },
          legend: { display: false }
        },
        scales: {
          y: { beginAtZero: true }
        }
      }
    });
  };

  const renderPie = (data) => {
    if (!window.Chart || !chartRef.current) return;
    const ctx = chartRef.current.getContext('2d');

    const colors = [
      'rgba(255, 99, 132, 0.8)',
      'rgba(54, 162, 235, 0.8)',
      'rgba(255, 206, 86, 0.8)',
      'rgba(75, 192, 192, 0.8)',
      'rgba(153, 102, 255, 0.8)',
      'rgba(255, 159, 64, 0.8)',
      'rgba(199, 199, 199, 0.8)',
      'rgba(83, 102, 255, 0.8)',
      'rgba(255, 99, 255, 0.8)',
      'rgba(99, 255, 132, 0.8)',
    ];

    const labels = (data.labels || []).slice(0, 10);
    const values = (data.values || []).slice(0, 10);
    const total = values.reduce((a, b) => a + b, 0);

    chartInstanceRef.current = new window.Chart(ctx, {
      type: 'pie',
      data: {
        labels: labels,
        datasets: [{
          data: values,
          backgroundColor: colors.slice(0, labels.length),
          borderWidth: 2,
          borderColor: '#fff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: `원그래프 - ${chartData.file?.name || ''}`, font: { size: 14 } },
          legend: { 
            display: true, 
            position: 'right',
            labels: { 
              font: { size: 11 },
              generateLabels: (chart) => {
                const dataset = chart.data.datasets[0];
                return chart.data.labels.map((label, i) => {
                  const value = dataset.data[i];
                  const percentage = ((value / total) * 100).toFixed(1);
                  return {
                    text: `${label}: ${value.toLocaleString()}원 (${percentage}%)`,
                    fillStyle: dataset.backgroundColor[i],
                    strokeStyle: '#fff',
                    lineWidth: 1,
                    index: i
                  };
                });
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const percentage = ((context.raw / total) * 100).toFixed(1);
                return `${context.label}: ${context.raw.toLocaleString()}원 (${percentage}%)`;
              }
            }
          }
        }
      }
    });
  };

  const renderHistogram = (data) => {
    if (!window.Chart || !chartRef.current) return;
    const ctx = chartRef.current.getContext('2d');

    chartInstanceRef.current = new window.Chart(ctx, {
      type: 'bar',
      data: {
        labels: data.bins || [],
        datasets: [{
          label: '빈도',
          data: data.counts || [],
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: `히스토그램 - ${chartData.file?.name || ''}` },
          legend: { display: false }
        },
        scales: {
          y: { beginAtZero: true, title: { display: true, text: '빈도' } }
        }
      }
    });
  };

  const renderLine = (data) => {
    if (!window.Chart || !chartRef.current) return;
    const ctx = chartRef.current.getContext('2d');

    chartInstanceRef.current = new window.Chart(ctx, {
      type: 'line',
      data: {
        labels: data.labels || [],
        datasets: [{
          label: data.columnName || '값',
          data: data.values || [],
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          fill: true,
          tension: 0.3
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: `선그래프 - ${chartData.file?.name || ''}` }
        },
        scales: {
          y: { beginAtZero: true }
        }
      }
    });
  };

  const renderBoxplot = (data) => {
    if (!window.Plotly || !plotRef.current) return;

    const traces = Object.entries(data).map(([column, stats]) => ({
      y: [stats.min, stats.q1, stats.median, stats.q3, stats.max],
      type: 'box',
      name: column,
      boxpoints: 'outliers'
    }));

    window.Plotly.newPlot(plotRef.current, traces, {
      title: `박스플롯 - ${chartData.file?.name || ''}`,
      height: 400,
      margin: { t: 50, b: 50, l: 60, r: 30 }
    }, { responsive: true, displayModeBar: false });
  };

  const renderScatter = (data) => {
    if (!window.Plotly || !plotRef.current) return;

    const trace = {
      x: (data.points || []).map(p => p.x),
      y: (data.points || []).map(p => p.y),
      mode: 'markers',
      type: 'scatter',
      marker: { size: 6, color: 'rgba(54, 162, 235, 0.7)' }
    };

    window.Plotly.newPlot(plotRef.current, [trace], {
      title: `산점도 - ${chartData.file?.name || ''}`,
      xaxis: { title: data.xLabel },
      yaxis: { title: data.yLabel },
      height: 400,
      margin: { t: 50, b: 50, l: 60, r: 30 }
    }, { responsive: true, displayModeBar: false });
  };

  const getChartTypeLabel = (type) => {
    const labels = {
      bar: '막대그래프', pie: '원그래프', histogram: '히스토그램',
      line: '선그래프', boxplot: '박스플롯', scatter: '산점도'
    };
    return labels[type] || type;
  };

  const usesPlotly = chartData?.chartType === 'boxplot' || chartData?.chartType === 'scatter';
  const usesChartJs = !usesPlotly;

  if (!chartData) return null;

  return (
    <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Chip
          label={getChartTypeLabel(chartData.chartType)}
          color="primary"
          size="small"
        />
        <Typography variant="body2" color="text.secondary">
          {chartData.file?.name}
        </Typography>
      </Box>

      {renderError && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          차트 렌더링 오류: {renderError}
        </Typography>
      )}

      {/* 차트 렌더링 영역 */}
      <Box sx={{ position: 'relative', height: 400, mb: 2 }}>
        {usesPlotly && (
          <div ref={plotRef} style={{ width: '100%', height: '100%' }} />
        )}
        {usesChartJs && (
          <canvas ref={chartRef} style={{ width: '100%', height: '100%' }} />
        )}
      </Box>

      {/* 데이터 요약 */}
      {chartData.analysis && (
        <Box sx={{ mt: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            데이터: {chartData.analysis.summary?.totalRows}행 × {chartData.analysis.summary?.totalColumns}열
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ChatChart;
